import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Paperclip, Plus, Trash } from "@phosphor-icons/react";
import { api, schick } from "../api/client";
import type { Besprechung, Ergaenzung } from "../api/types";
import { datum, heute } from "../lib/format";
import { Auswahl } from "./Auswahl";
import { Dialog, Feld, Meldung } from "./Bausteine";
import { Diktat } from "./Diktat";

/**
 * Ergänzungen nach der Freigabe (SPEC §4.12): was nach der Besprechung noch per Mail
 * kommt. Das Protokoll bleibt eingefroren; die Ergänzung steht daneben und wandert beim
 * Fortschreiben in den Punkt der nächsten Besprechung.
 */
export function ErgaenzungListe({ liste, kann, geaendert }: {
  liste: Ergaenzung[]; kann: boolean; geaendert: (b: Besprechung) => void;
}) {
  const entfernen = useMutation({
    mutationFn: (id: string) => api<Besprechung>("DELETE", `/ergaenzungen/${id}`),
    onSuccess: geaendert,
  });
  if (!liste.length) return null;
  return (
    <div className="ergaenzungen">
      {liste.map((g) => (
        <div key={g.id} className="ergaenzung">
          <div className="ergaenzung-kopf">
            <b>Ergänzung {datum(g.am)}</b>{g.quelle && <span> · {g.quelle}</span>}
            {g.datei_id && (
              <a href={`/api/ergaenzungen/${g.id}/datei`} className="ergaenzung-beleg" title="Beleg herunterladen">
                <Paperclip size={13} /> {g.datei_name}</a>
            )}
            <span className="mute">{g.uebernommen_nr ? `→ in Nr. ${g.uebernommen_nr}` : "→ kommt in die nächste Besprechung"}</span>
            {kann && !g.uebernommen_in && (
              <button className="knopf leise symbol" aria-label="Ergänzung entfernen"
                      onClick={() => confirm("Diese Ergänzung entfernen?") && entfernen.mutate(g.id)}><Trash size={14} /></button>
            )}
          </div>
          <div className="ergaenzung-text">{g.text}</div>
        </div>
      ))}
      <Meldung fehler={entfernen.error} />
    </div>
  );
}

export function ErgaenzungKnopf({ onClick }: { onClick: () => void }) {
  return <button className="knopf leise" title="Ergänzung nach der Freigabe, z. B. aus einer Mail" onClick={onClick}>
    <Plus size={15} /> Ergänzung</button>;
}

export function ErgaenzungDialog({ b, eintragId, zu }: { b: Besprechung; eintragId: string; zu: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ eintrag_id: eintragId, am: heute(), quelle: "", text: "" });
  const [datei, setDatei] = useState<File | null>(null);
  const erfassen = useMutation({
    mutationFn: () => {
      const d = new FormData();
      Object.entries(f).forEach(([k, v]) => d.append(k, v));
      if (datei) d.append("datei", datei);
      return schick<Besprechung>(`/besprechungen/${b.id}/ergaenzungen`, d);
    },
    onSuccess: (neu) => {
      qc.setQueryData(["besprechung", b.id], { ...neu, meine_rolle: b.meine_rolle });
      // Steht schon ein Entwurf, ist die Ergänzung dort sofort eingearbeitet.
      qc.invalidateQueries({ queryKey: ["besprechung"], predicate: (q) => q.queryKey[1] !== b.id });
      zu();
    },
  });
  const punkte = b.themen.flatMap((t) => t.eintraege);
  return (
    <Dialog offen zu={zu} titel={`Ergänzung zu Protokoll Nr. ${b.nummer}`} fuss={<>
      <button className="knopf leise" onClick={zu}>Abbrechen</button>
      <button className="knopf haupt" disabled={!f.text.trim() || erfassen.isPending} onClick={() => erfassen.mutate()}>Erfassen</button>
    </>}>
      <div className="raster">
        <Feld titel="Zu Punkt" breit>
          <Auswahl value={f.eintrag_id} onChange={(e) => setF({ ...f, eintrag_id: e.target.value })}>
            <option value="">zum Protokoll allgemein</option>
            {punkte.map((e) => <option key={e.id} value={e.id}>{e.nummer} {(e.titel || e.text || e.verlauf.at(-1)?.text || "").slice(0, 70)}</option>)}
          </Auswahl>
        </Feld>
        <Feld titel="Eingegangen am"><input type="date" className="eingabe" value={f.am} onChange={(e) => setF({ ...f, am: e.target.value })} /></Feld>
        <Feld titel="Quelle"><input className="eingabe" placeholder="z. B. Mail Fr. Schlierkamp" value={f.quelle}
                                    onChange={(e) => setF({ ...f, quelle: e.target.value })} /></Feld>
        <div className="feld breit">
          <div className="feld-kopf"><span>Ergänzung</span>
            <Diktat text={f.text} speichern={(v) => setF((x) => ({ ...x, text: v }))} /></div>
          <textarea className="eingabe" rows={4} aria-label="Ergänzung" autoFocus value={f.text}
                    onChange={(e) => setF({ ...f, text: e.target.value })} />
        </div>
        <label className="feld breit"><span>Beleg (Mail als .msg/.eml, PDF …), optional</span>
          <input type="file" className="eingabe" accept=".msg,.eml,.pdf,.png,.jpg,.jpeg,.docx,.xlsx"
                 onChange={(e) => setDatei(e.target.files?.[0] ?? null)} /></label>
      </div>
      <p className="mute">Das freigegebene Protokoll bleibt unverändert. Die Ergänzung steht am Punkt und in der
        Druckansicht und wird beim Anlegen der nächsten Besprechung in den Punkt übernommen.</p>
      <Meldung fehler={erfassen.error} />
    </Dialog>
  );
}
